import type { PlayTreeNode, PlayTreeRankHighlight } from './DiffereStatsPlayTreeDrill';

const NOTABLE_RANK_KINDS = new Set<PlayTreeRankHighlight['kind']>([
  'first',
  'second',
  'third',
  'last',
]);

export function hasNotableRankHighlight(node: PlayTreeNode): boolean {
  return (node.rankHighlights ?? []).some(h => NOTABLE_RANK_KINDS.has(h.kind));
}

/**
 * Masque les hypothèses avec effectif &lt; minCount sauf si :
 * - 1er / 2e / 3e / dernier, ou
 * - effectif = Seq. max (séquence dominante, même sous le seuil).
 */
export function shouldShowPlayTreeHypothesis(
  node: PlayTreeNode,
  minCount: number,
  hideLowCount: boolean,
  maxSequenceCount = 0,
): boolean {
  if (!hideLowCount) return true;
  if (node.count >= minCount) return true;
  if (maxSequenceCount > 0 && node.count === maxSequenceCount) return true;
  return hasNotableRankHighlight(node);
}

export function collapseExpandedDescendants(
  expanded: Set<string>,
  pathKey: string,
): Set<string> {
  const next = new Set(expanded);
  for (const k of expanded) {
    if (k === pathKey || k.startsWith(`${pathKey}|`)) next.delete(k);
  }
  return next;
}

export function toggleExpandedPath(expanded: Set<string>, pathKey: string): Set<string> {
  const next = new Set(expanded);
  if (next.has(pathKey)) {
    return collapseExpandedDescendants(next, pathKey);
  }
  next.add(pathKey);
  return next;
}

export interface PlayTreePathRow {
  path: string[];
  node: PlayTreeNode;
}

/** Effectif de la séquence dominante (= Seq. max) parmi les hypothèses aplaties. */
export function maxSequenceCountAmongPaths(rows: PlayTreePathRow[]): number {
  let max = 0;
  for (const row of rows) {
    if (row.node.count > max) max = row.node.count;
  }
  return max;
}

/** Toutes les hypothèses complètes (depth plis) présentes dans l'arbre. */
export function flattenPlayTreePaths(
  root: PlayTreeNode,
  maxDepth: number,
): PlayTreePathRow[] {
  const rows: PlayTreePathRow[] = [];

  function walk(nodes: PlayTreeNode[], keys: string[]) {
    for (const node of nodes) {
      const path = [...keys, node.key];
      if (node.depth === maxDepth) {
        rows.push({ path, node });
      } else if (node.children.length > 0 && node.depth < maxDepth) {
        walk(node.children, path);
      }
    }
  }

  walk(root.children, []);
  return rows;
}

export type PlayTreePathSort = 'total' | 'topDecile';

export function sortPlayTreePaths(
  rows: PlayTreePathRow[],
  sort: PlayTreePathSort,
): PlayTreePathRow[] {
  const copy = [...rows];
  if (sort === 'topDecile') {
    copy.sort((a, b) => {
      const d1 = (b.node.decileCounts?.[0] ?? 0) - (a.node.decileCounts?.[0] ?? 0);
      if (d1 !== 0) return d1;
      return b.node.count - a.node.count;
    });
  } else {
    copy.sort((a, b) => b.node.count - a.node.count);
  }
  return copy;
}
